import {
  CheckCircleFilled,
  ImportOutlined, InboxOutlined, WarningOutlined
} from '@ant-design/icons';
import {
  Button, Card, message, Modal, Space, Spin, Typography,
  UploadProps
} from 'antd';
import { ColumnProps } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { importStaffAssignService, importStaffAssignTemplateService } from 'common/services/extends/staffAssign';
import { ImportStaffAssignResponseType } from 'common/services/extends/staffAssign/types';
import { NoTaxCode, ROUTE_PATHS } from 'common/utils/constant';
import roles, { getPermission } from 'configs/roles';

type ImportTable = {
  key: number,
  nameCol: string,
  CCCDCol: string,
  taxCode?: string,
  position: string,
  permanentAddress: string,
  phoneNumberCol: string,
  status: 'invalid' | 'valid',
  errorStatusCol?: Array<string>,
};

const EmployeeAssignImport: React.FC<ActiveRoles> = ({ roleOther }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [fileList, setFileList] = React.useState<RcFile | null>(null);
  const [dataList, setDataList] = React.useState<ImportStaffAssignResponseType | null>(null);

  const { mutate: importStaffAssignTemplateMutate, isLoading: loadingTemplate } = useMutation(
    ['imporStaffAssignTemplate'],
    () => importStaffAssignTemplateService(),
    {
      onSuccess: (data) => {
        if (data?.link) {
          window.open(data.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const {
    mutate: importStaffAssignMutate,
    isLoading: loadingStaffAssign
  } = useMutation(
    ['importStaffAssignMutate'],
    async (param: {
      file: File,
      preview: boolean
    }) => importStaffAssignService({
      file: param.file,
      preview: param.preview,
    }),
    {
      onSuccess: (data, variables) => {
        if (variables.preview) {
          setDataList(data);
        } else {
          message.success(t('message.updateSuccess'));
          setFileList(null);
          setDataList(null);
          navigate(ROUTE_PATHS.EMPLOYEE_ASSIGN_MANAGEMENT);
        }
      },
      onError: () => {
        message.error(t('message.uploadError'));
      }
    }
  );

  const handleBeforeUpload = async (fileRes: RcFile): Promise<boolean> => {
    try {
      setFileList(fileRes);
      importStaffAssignMutate({
        file: fileRes,
        preview: true,
      });
    } catch {
      message.error('Import failed');
    }
    return false;
  };
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    beforeUpload: handleBeforeUpload,
    disabled: loadingStaffAssign,
  };

  const columns: ColumnProps<ImportTable>[] = [
    {
      title: t('contact.problem'),
      dataIndex: 'statusCol',
      key: 'statusCol',
      width: 380,
      fixed: 'left',
      sorter: {
        compare: (a: ImportTable, b: ImportTable) => a.status.localeCompare(b.status),
      },
      render: (text, record) => {
        if (record.status === 'valid') {
          return (
            <Space>
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Typography.Text>Hợp lệ</Typography.Text>
            </Space>
          );
        }
        return (
          <ul style={{ paddingLeft: 0 }}>
            {
              record.errorStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f5222d' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
          </ul>
        );
      }
    },
    {
      title: t('system.name'),
      dataIndex: 'nameCol',
      key: 'nameCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('system.phone'),
      dataIndex: 'phoneNumberCol',
      key: 'phoneNumberCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('employee.identityNumber'),
      dataIndex: 'CCCDCol',
      key: 'CCCDCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('employee.taxNumber'),
      dataIndex: 'taxCode',
      key: 'taxCode',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('employee.permanentAddress'),
      dataIndex: 'permanentAddress',
      key: 'permanentAddress',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('system.position'),
      dataIndex: 'position',
      key: 'position',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
  ];

  const tableData = useMemo(() => {
    if (dataList) {
      return dataList.rows.map((item, idx): ImportTable => ({
        key: idx,
        nameCol: item.fullname,
        phoneNumberCol: item.phoneNumber,
        CCCDCol: item.idCardNumber,
        status: item.errors.length > 0 ? 'invalid' : 'valid',
        errorStatusCol: item.errors,
        position: item.position,
        permanentAddress: item.permanentAddress,
        taxCode: item.taxCode || NoTaxCode.label
      }));
    }
    return [];
  }, [dataList]);

  return (
    <div className="p-employeeImport">
      <HeaderPage
        fixed
        title={t('employee.employeeAssignImport')}
        rightHeader={(
          dataList && (
            <Space>
              <Button
                type="primary"
                disabled={!dataList.rows.length || dataList.errors > 0
                  || !getPermission(roleOther, roles.EMPLOYEE_ASSIGN_IMPORT)}
                onClick={() => {
                  Modal.confirm({
                    className: 't-pagetable_deleteLanguageModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('employee.importConfirm'),
                    onOk: () => {
                      if (fileList) {
                        importStaffAssignMutate({
                          file: fileList,
                          preview: false,
                        });
                      }
                    },
                  });
                }}
              >
                {t('system.ok')}
              </Button>
              <Button onClick={() => setDataList(null)} disabled={loadingStaffAssign}>
                {t('system.cancel')}
              </Button>
            </Space>
          ))}
      />
      <Spin spinning={loadingStaffAssign}>
        <div className="t-mainlayout_wrapper">
          <Card
            className="p-employeeImport_card"
            title={(
              <Space style={{ width: '100%', justifyContent: 'space-between' }} direction="horizontal">
                {t('system.import')}
                {dataList ? null : (
                  <Button
                    type="primary"
                    disabled={loadingTemplate
                      || !getPermission(roleOther, roles.EMPLOYEE_ASSIGN_IMPORT)}
                    onClick={() => importStaffAssignTemplateMutate()}
                    loading={loadingTemplate}
                  >
                    <Space>
                      <ImportOutlined />
                      {t('system.downloadSampleFile')}
                    </Space>
                  </Button>
                )}
              </Space>
            )}
          >
            {dataList ? (
              <div>
                <div className="p-employeeImport_card_general">
                  <Space className="p-employeeImport_card_general-item" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Tổng số dòng dữ liệu</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <Typography.Text>{dataList.total}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item success" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng hợp lệ</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <CheckCircleFilled style={{ color: '#52c41a' }} />
                      <Typography.Text>{dataList.total - dataList.errors}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item error" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng lỗi</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <WarningOutlined style={{ color: '#f5222d' }} />
                      <Typography.Text>
                        {dataList.errors}
                      </Typography.Text>
                    </Space>
                  </Space>
                </div>
                <div className="p-employeeImport_card_table">
                  <PageTable
                    noCheckbox
                    isLoading={false}
                    tableProps={{
                      columns,
                      pageData: tableData || [],
                      initShowColumns: ['statusCol', 'nameCol', 'phoneNumberCol', 'permanentAddress', 'CCCDCol', 'taxCode', 'position'],
                      noBaseCol: true,
                      noDeleteLanguage: true,
                      rowClassName: (record) => (record.status === 'valid' ? 'p-employeeImport_card_table-validRow' : 'p-employeeImport_card_table-invalidRow'),
                      isHidePagination: true
                    }}
                  />
                </div>
              </div>
            ) : (
              <Dragger
                {...uploadProps}
                disabled={loadingStaffAssign
                  || !getPermission(roleOther, roles.EMPLOYEE_ASSIGN_IMPORT)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {' '}
                  {t('media.dndText')}
                </p>
              </Dragger>
            )}
          </Card>
        </div>
      </Spin>
    </div>
  );
};

export default EmployeeAssignImport;
